import React, { useRef, useCallback } from "react"
import { Editor, EditorHandle } from "@/editor/Editor"
import { getChartToolData, getAiContentData } from "@/editor/editorConfig"
import { useUpdateQuarterlyReportContent } from "~/api/quarterly_reports"
import { ReportSection } from "~/api/communications"
import QuarterlyReportLayout from "./QuarterlyReportLayout"
import PdfPageWrapper from "./PdfPageWrapper"
import { useCommunicationContext } from "./CommunicationContext"
import { useQuarterlyReportContext } from "./useQuarterlyReportContext"
import { usePdfView } from "./usePdfView"

const LpInvestmentPerformance: React.FC = () => {
  const { communicationId } = useCommunicationContext()
  const { reportId, report, isLoading, error, mock } =
    useQuarterlyReportContext()
  const editorRef = useRef<EditorHandle>(null)
  const updateReportContent = useUpdateQuarterlyReportContent()
  const { showPdfView, pdfLoading, pdfUrl, togglePdfView, loadPdfView } =
    usePdfView({
      reportId,
      sectionName: ReportSection.LP_INVESTMENT_PERFORMANCE,
      editorContent:
        report?.section_content?.lp_investment_performance?.editor_content,
      mock,
    })

  const onChange = useCallback(async () => {
    const content = await editorRef?.current?.getContent()
    if (!content || !reportId) return

    console.log("updating with content: ", content)
    updateReportContent.mutate({
      id: reportId,
      lp_investment_performance_editorjs_content: content,
    })
  }, [reportId, updateReportContent])

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading KPIs & Highlights...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Failed to load report: {error.message}
      </div>
    )
  }

  if (!report) {
    return (
      <div className="alert alert-warning" role="alert">
        Report not found
      </div>
    )
  }

  return (
    <QuarterlyReportLayout
      title="LP Investment Performance"
      subtitle={`Edit the LP Investment Performance section for ${report.household_name}`}
      showEditorActions={true}
      reportId={reportId}
      showPdfView={showPdfView}
      pdfLoading={pdfLoading}
      pdfUrl={pdfUrl}
      onTogglePdfView={togglePdfView}
      breadcrumbProps={{
        householdName: report.household_name,
        householdPath: `/manage/communications/${communicationId}/quarterly_reports/${reportId}`,
        sectionName: ReportSection.LP_INVESTMENT_PERFORMANCE,
      }}
    >
      <PdfPageWrapper
        sectionTitle="LP Investment Performance"
        onPdfViewClick={loadPdfView}
      >
        <Editor
          ref={editorRef}
          initialData={
            report.section_content?.lp_investment_performance?.editor_content
          }
          aiContentData={getAiContentData(report)}
          chartToolData={getChartToolData(report)}
          onChange={onChange}
          reportId={reportId}
        />
      </PdfPageWrapper>
    </QuarterlyReportLayout>
  )
}

export default LpInvestmentPerformance
